export type TranscriptStep = {
  timecode: number;
  rawTimecode: string;
  speaker: string;
  track: "caller" | "callee";
  speakerId: number;
  message: string;
};

export type Transcript = {
  rawTranscript: string;
  steps: TranscriptStep[];
  failed: boolean;
};

export function transcriptParser(transcription: string): Transcript {
  if (!transcription) {
    return {
      rawTranscript: "",
      steps: [],
      failed: true,
    };
  }

  const parseTimecodeToSeconds = (rawTimecode: string) => {
    const parts = rawTimecode.split(":");

    if (parts.length === 2) {
      const minutes = Number(parts[0]);
      const seconds = Number(parts[1]);
      return minutes * 60 + seconds;
    }

    if (parts.length === 3) {
      const hours = Number(parts[0]);
      const minutes = Number(parts[1]);
      const seconds = Number(parts[2]);
      return hours * 3600 + minutes * 60 + seconds;
    }

    return 0;
  };

  const markerRegex = /\[(\d{1,2}:\d{2}(?::\d{2})?)\]\s*([^:\]]+):\s*/g;

  const extractSpeakerInfo = (speaker: string) => {
    const match = speaker.match(/^(Caller|Callee)(?:\s*\(([^)]+)\))?$/i);

    if (match) {
      const normalizedTrack = match[1].toLowerCase() as "caller" | "callee";
      return {
        speaker: match[2]?.trim() || match[1],
        track: normalizedTrack,
      };
    }

    // Transcript markers are always caller/callee; default to caller if formatting is unexpected.
    return { speaker: speaker.trim(), track: "caller" as const };
  };

  const speakerIds = new Map<string, number>();
  let nextSpeakerId = 1;
  const markers: {
    timecode: number;
    rawTimecode: string;
    speaker: string;
    track: "caller" | "callee";
    markerIndex: number;
    contentStart: number;
  }[] = [];

  let match = markerRegex.exec(transcription);

  while (match) {
    const parsedSpeaker = extractSpeakerInfo(match[2].trim());

    markers.push({
      timecode: parseTimecodeToSeconds(match[1]),
      rawTimecode: match[1],
      speaker: parsedSpeaker.speaker,
      track: parsedSpeaker.track,
      markerIndex: match.index,
      contentStart: markerRegex.lastIndex,
    });

    match = markerRegex.exec(transcription);
  }

  const steps = markers.map((marker, index) => {
    const nextMarkerIndex = index < markers.length - 1 ? markers[index + 1].markerIndex : transcription.length;
    const message = transcription.slice(marker.contentStart, nextMarkerIndex).trim();
    const normalizedSpeaker = marker.speaker.toLowerCase();

    if (!speakerIds.has(normalizedSpeaker)) {
      speakerIds.set(normalizedSpeaker, nextSpeakerId);
      nextSpeakerId += 1;
    }

    return {
      timecode: marker.timecode,
      rawTimecode: marker.rawTimecode,
      speaker: marker.speaker,
      track: marker.track,
      speakerId: speakerIds.get(normalizedSpeaker) as number,
      message,
    };
  });

  return {
    rawTranscript: transcription,
    steps,
    failed: transcription.trim().length > 0 && steps.length === 0,
  };
}
